import { ChangeDetectorRef, Component, ElementRef, Injector, Input, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TrainingIntervalTypeEnum, TrainingListItem, TrainingResponseList, TrainingTypeEnum } from '@trendbuild/trend-gpt-maker-api/src/lib/models/training.types';
import { AbstractComponent, AgentTrendGPTService, AlertModalComponent, ConfirmationComponent, FirebaseService, UploadTypeEnum, UtilHelper } from 'lib-trend-core';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-agent-training-form',
    templateUrl: './agent-training-form.component.html',
    styleUrls: ['./agent-training-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentTrainingFormComponent extends AbstractComponent {
  @ViewChild('inputFile', { static: false }) inputFile: ElementRef<HTMLInputElement>;

  @Input() agentId!: string;
  @Input() currentStep: number;
  @Input() stepChange: (step: number) => void;

  public trainingList: TrainingResponseList;
  public page = { page: 1, pageSize: 10 };
  public activeTrainingType: TrainingTypeEnum = TrainingTypeEnum.TEXT;
  public TrainingTypeEnum = TrainingTypeEnum;

  editingTrainingId: string = null;

  filename = '';
  contentType = '';
  fileSizeMb = '';
  fileLoaded = false;
  document: { documentName: string; documentUrl: string; documentMimetype: string }[] = [];
  editMode = signal(false);

  interval: Array<{ label: string; value: TrainingIntervalTypeEnum }> = [
    { label: 'agents.trainingForm.intervalNever', value: TrainingIntervalTypeEnum.NEVER },
    { label: 'agents.trainingForm.interval1h', value: TrainingIntervalTypeEnum.ONE_HOUR },
    { label: 'agents.trainingForm.interval4h', value: TrainingIntervalTypeEnum.FOUR_HOUR },
    { label: 'agents.trainingForm.interval8h', value: TrainingIntervalTypeEnum.EIGHT_HOUR },
    { label: 'agents.trainingForm.interval12h', value: TrainingIntervalTypeEnum.TWELVE_HOUR },
    { label: 'agents.trainingForm.interval1d', value: TrainingIntervalTypeEnum.ONE_DAY },
    { label: 'agents.trainingForm.interval1w', value: TrainingIntervalTypeEnum.ONE_WEEK },
    { label: 'agents.trainingForm.interval1mo', value: TrainingIntervalTypeEnum.ONE_MONTH },
  ];

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private firebaseService: FirebaseService,
    private modalAlert: MatDialog,
    private agentTrendGPTService: AgentTrendGPTService,
  ) {
    super(injector);
    this.setupForm();
  }

  handleStepChange(step: number): void {
    this.stepChange(step);
  }

  ngOnInit() {
    this.getTrainingByAgentId();
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      type: [TrainingTypeEnum.TEXT, []],
      text: [null, []],
      website: [null, [Validators.pattern('https?:?//.+')]],
      video: [null, [Validators.pattern('https?:?//.+')]],
      trainingInterval: [],
      trainingSubPages: [null],
      document: [null, []],
      callbackUrl: [null, []],
    });

    this.formGroup.valueChanges.subscribe((value) => { });
  }

  private async getTrainingByAgentId() {
    this.trainingList = null;
    this.trainingList = await firstValueFrom(this.agentTrendGPTService.getTrainingByAgentId(
      this.agentId,
      {
        type: this.activeTrainingType,
        page: this.page.page,
        pageSize: this.page.pageSize,
      }
    )) as TrainingResponseList;
    return this.trainingList;
  }


  handleFileSelect(event: Event): void {
    const file = (event.target as HTMLInputElement).files[0]

    if (!file) {
      this.clearFileState();
      return;
    }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(file.type);
    if (fileTypeLimit && file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      this.clearFileState();
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        this.loading = true;

        const dateMilisecond = new Date().getTime();
        const filename: string = `${file.name}_${dateMilisecond}`;
        this.filename = file.name;
        this.contentType = file.type;
        this.fileSizeMb = this.formatBytes(file.size);
        this.fileLoaded = true;

        this.firebaseService.uploadFile(file, filename, UploadTypeEnum.CSV).then((snapshot: { url: string }) => {
          this.document.push({
            documentName: this.filename,
            documentUrl: snapshot.url,
            documentMimetype: this.contentType,
          });
          this.loading = false;
          this.cdr.detectChanges();
        }).catch(error => {
          this.alertService.error(this.t('agents.trainingForm.toastUploadError'));
          this.loading = false;
          this.clearFileState();
        });
      } catch (error) {
        this.alertService.error(this.t('agents.trainingForm.toastProcessError'));
        this.clearFileState();
      } finally {
        this.loading = false;
      }
    };

    reader.readAsText(file, 'UTF-8');
  }

  clearFileState(): void {
    this.filename = '';
    this.contentType = '';
    this.fileSizeMb = '';
    this.fileLoaded = false;
    this.document = [];

    if (this.inputFile && this.inputFile.nativeElement) {
      this.inputFile.nativeElement.value = '';
    }
    this.cdr.detectChanges();
  }

  changeTrainingType(type: TrainingTypeEnum): void {
    this.activeTrainingType = type;
    this.getTrainingByAgentId();
  }

  editTextTraining(training: any): void {
    this.editMode.set(true);
    this.formGroup.get('text').setValue(training.text);
    const textElement = document.getElementById('trainingText');
    if (textElement) {
      textElement.scrollIntoView({ behavior: 'smooth' });
      textElement.focus();
    }
    this.editingTrainingId = training.id;
  }

  cancelEditText(): void {
    this.formGroup.get('text').setValue(null);
    this.editingTrainingId = null;
    this.alertService.info(this.t('agents.trainingForm.toastEditCancelled'));
    this.editMode.set(false);
  }

  async addTrainingText(): Promise<void> {
    const { text } = this.formGroup.getRawValue();
    const payload: Partial<TrainingListItem> = { type: TrainingTypeEnum.TEXT, text: text };

    try {
      this.loading = true;

      if (this.editingTrainingId) {
        await firstValueFrom(this.agentTrendGPTService.deleteTrainingById(this.editingTrainingId));
        await firstValueFrom(this.agentTrendGPTService.createTrainingText(this.agentId, payload));
        this.editMode.set(false);
        this.alertService.success(this.t('agents.trainingForm.toastTrainingUpdated'));
        this.editingTrainingId = null;
      } else {
        await firstValueFrom(this.agentTrendGPTService.createTrainingText(this.agentId, payload));
        this.alertService.success(this.t('agents.trainingForm.toastTrainingAdded'));
      }

      this.formGroup.reset({ type: TrainingTypeEnum.TEXT, text: null });
      await this.getTrainingByAgentId();
      this.cdr.detectChanges();
    } catch (error: any) {
      this.alertService.error(this.t(this.editingTrainingId ? 'agents.trainingForm.toastUpdateTrainingError' : 'agents.trainingForm.toastAddTrainingError', { error: (error?.error?.message || error.message) }));
    } finally {
      this.loading = false;
      this.editMode.set(false);
    }
  }

  async addTrainingWebsite(): Promise<void> {
    const { website } = this.formGroup.getRawValue();
    const payload: Partial<TrainingListItem> = { type: TrainingTypeEnum.WEBSITE, website: website, };


    try {
      this.loading = true;
      await firstValueFrom(this.agentTrendGPTService.createTrainingSite(this.agentId, payload));

      this.formGroup.reset({ type: TrainingTypeEnum.WEBSITE, website: null });
      this.alertService.success(this.t('agents.trainingForm.toastTrainingAdded'));
      await this.getTrainingByAgentId();
      this.cdr.detectChanges();
    } catch (error: any) {
      this.alertService.error(this.t('agents.trainingForm.toastAddTrainingError', { error: (error?.error?.message || error.message) }));
    } finally {
      this.loading = false;
    }
  }

  async addTrainingVideo(): Promise<void> {
    const { video } = this.formGroup.getRawValue();
    const payload: Partial<TrainingListItem> = { type: TrainingTypeEnum.VIDEO, video: video, };

    try {
      this.loading = true;
      await firstValueFrom(this.agentTrendGPTService.createTrainingVideo(this.agentId, payload));
      this.formGroup.reset({ type: TrainingTypeEnum.VIDEO, video: null });

      this.alertService.success(this.t('agents.trainingForm.toastTrainingAdded'));
      await this.getTrainingByAgentId();
      this.cdr.detectChanges();
    } catch (error: any) {
      this.alertService.error(this.t('agents.trainingForm.toastAddTrainingError', { error: (error?.error?.message || error.message) }));
    } finally {
      this.loading = false;
    }
  }

  async addTrainingDocument(): Promise<void> {
    if (!this.document || this.document.length === 0 || !this.fileLoaded) {
      this.alertService.error(this.t('agents.trainingForm.toastNoFileSelected'));
      return;
    }

    try {
      this.loading = true;
      const documentData = this.document[this.document.length - 1];

      const payload: Partial<TrainingListItem> = {
        type: TrainingTypeEnum.DOCUMENT,
        documentUrl: documentData.documentUrl,
        documentName: documentData.documentName,
        documentMimetype: documentData.documentMimetype,
      };
      await firstValueFrom(this.agentTrendGPTService.createTrainingDocument(this.agentId, payload));

      this.clearFileState();

      this.alertService.success(this.t('agents.trainingForm.toastFileUploaded'));
      await this.getTrainingByAgentId();
      this.cdr.detectChanges();
    } catch (error: any) {
      this.alertService.error(this.t('agents.trainingForm.toastAddTrainingError', { error: (error?.error?.message || error.message) }));
    } finally {
      this.loading = false;
    }
  }

  delete(trainingId: string): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      try {
        await firstValueFrom(this.agentTrendGPTService.deleteTrainingById(trainingId));
        await this.getTrainingByAgentId();
        this.cdr.detectChanges();
      } catch (error: any) {
        this.alertService.error(this.t('agents.trainingForm.toastDeleteTrainingError', { error: (error?.error?.message || error.message) }));
      }
    });
  }

  private openModalAlert(title: string, message: string): void {
    const dialogRefAlert = this.modalAlert.open(AlertModalComponent, {
      width: '600px',
      maxHeight: '300px',
      data: { title, message },
    });
    dialogRefAlert.afterClosed().subscribe(() => this.inputFile.nativeElement.value = '');
  }

  private formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }
}
